/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

import styled from '@emotion/styled'
import React, { useEffect, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import {
  avaIntroStepSelectors,
  updateAvaIntroStepAction,
  updateAvaStepAction,
} from 'src/redux/data/ava/step'
import { isMobileOnly } from 'react-device-detect'
import { css } from '@emotion/core'
import {
  avaIntroIndexSelectors,
  updateAvaIntroIndex,
} from 'src/redux/data/ava/questionIndex'
import { avaInterviewSelectors, avaSaveInfoAction } from '../../redux'
import UserNameComponent from '../components/intro/UserNameComponent'
import PositionDetailsComponent from '../components/intro/PositionDetailsComponent'
import Header from '../components/Header'
import PositionComponent from '../components/intro/PositionComponent'
import PracticeForCompanyComponent from '../components/intro/PracticeForCompanyComponent'
import QuestionComponent from '../components/intro/QuestionComponent'
import RoleComponent from '../components/intro/RoleComponent'
import { SpeechTexts } from '../speechService'
import GettingQuestionsComponent from '../components/intro/GettingQuestionsComponent'
import { AvaStep, InterviewPart } from '../models'
import RotatePhoneComponent from '../components/elements/RotatePhoneComponent'
import SettingsBar from '../components/elements/SettingsBar'
import ManagerComponent from '../components/intro/ManagerComponent'
import useAvaSound from '../services/avaSound'
import httpClient from '../../httpClient'
import subscriptionsController from '../../api/controllers/subscriptions.controller'
import { getAuthorizationToken } from '../../utils/jwtToken'
import SubscriptionComponent from '../components/intro/SubscriptionComponent'
import InterviewLimitModal from '../components/modals/InterviewLimitModal'

const Layout = styled.div`
  width: 100%;
  height: 100%;
  min-width: 600px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media screen and (max-width: 600px) {
    height: 100%;
  }

  ${isMobileOnly &&
  css`
    position: absolute;
    display: flex;
    flex-direction: column;
    background: #f6f4fd;
    min-height: 100%;
    min-width: 100%;
  `}
`

export enum IntroStep {
  UserName = 'hello',
  Subscription = 'subscription',
  Position = 'position',
  Role = 'role',
  IsManager = 'isManager',
  IsPracticeForCompany = 'isPracticeForCompany',
  PositionDetails = 'positionDetails',
  Experience = 'experience',
  GettingQuestions = 'gettingQuestions',
}

interface IStepData {
  step: IntroStep
  nextStep?: IntroStep
}

const StepsData: IStepData[] = [
  {
    step: IntroStep.UserName,
    nextStep: IntroStep.Position,
  },
  {
    step: IntroStep.Position,
    nextStep: IntroStep.Role,
  },
  {
    step: IntroStep.Role,
    nextStep: IntroStep.IsPracticeForCompany,
  },
  {
    step: IntroStep.IsManager,
    nextStep: IntroStep.IsPracticeForCompany,
  },
  {
    step: IntroStep.IsPracticeForCompany,
    nextStep: IntroStep.Experience,
  },
  {
    step: IntroStep.PositionDetails,
    nextStep: IntroStep.Experience,
  },
  {
    step: IntroStep.Experience,
    nextStep: IntroStep.Subscription,
  },
  {
    step: IntroStep.Subscription,
    nextStep: IntroStep.GettingQuestions,
  },
  {
    step: IntroStep.GettingQuestions,
    nextStep: null,
  },
]

function IntroContainer() {
  const dispatch = useDispatch()
  const { interviewToken } = useParams<{ interviewToken?: string }>()
  const { playSound } = useAvaSound()

  const interview = useSelector(avaInterviewSelectors.data)

  const step = useSelector(avaIntroStepSelectors.data)
  const questionIndex = useSelector(avaIntroIndexSelectors.data)
  const [backMode, setBackMode] = useState(false)
  const [showLimitModal, setShowLimitModal] = useState(false)
  const [hasSubscription] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [willShowSubscriptionStep, setWillShowSubscriptionStep] = useState(true) // Default to showing it

  const goNextStep = (currStep: IStepData) => {
    setBackMode(false)
    dispatch(updateAvaIntroStepAction(currStep.nextStep))
    dispatch(
      updateAvaIntroIndex({
        ...questionIndex,
        questionNumberText: '',
        questionNumber: questionIndex.questionNumber + 1,
      })
    )
  }

  const goToOnboarding = () => {
    dispatch(updateAvaStepAction(AvaStep.Onboarding))
  }

  const goPrevStep = (currStep: IStepData) => {
    setBackMode(true)
    switch (currStep.step) {
      case IntroStep.UserName:
        goToOnboarding()
        break
      case IntroStep.Subscription:
        dispatch(
          updateAvaIntroIndex({
            ...questionIndex,
            questionNumberText: '',
          })
        )
        dispatch(updateAvaIntroStepAction(IntroStep.UserName))
        break
      case IntroStep.IsManager:
        dispatch(
          updateAvaIntroIndex({
            ...questionIndex,
            questionNumberText: '',
          })
        )
        dispatch(updateAvaIntroStepAction(IntroStep.Role))
        break
      case IntroStep.PositionDetails:
        dispatch(
          updateAvaIntroIndex({
            ...questionIndex,
            questionNumberText: '',
          })
        )
        dispatch(updateAvaIntroStepAction(IntroStep.IsPracticeForCompany))
        break
      default:
        dispatch(
          updateAvaIntroStepAction(
            StepsData.find((s) => s.nextStep === currStep.step).step
          )
        )
        dispatch(
          updateAvaIntroIndex({
            ...questionIndex,
            questionNumber: questionIndex.questionNumber - 1,
          })
        )
    }
  }

  // Check if user is authenticated and skip UserName step
  useEffect(() => {
    const token = getAuthorizationToken()
    if (token) {
      // If we're on UserName step and user is authenticated, skip to next step
      // Backend will prefill the data when to_intro is called
      if (step === IntroStep.UserName) {
        const currentStepData = StepsData.find((s) => s.step === step)
        if (currentStepData) {
          // Trigger to_intro to let backend prefill data
          dispatch(
            avaSaveInfoAction({
              interviewToken,
              params: { to_intro: true },
            })
          )
          goNextStep(currentStepData)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, interviewToken, dispatch])

  // Check subscription status to determine if subscription step will be shown
  useEffect(() => {
    const checkSubscriptionForCounter = async () => {
      if (!interview || !interview.candidate?.email) {
        // No email yet, default to showing subscription step (06/06)
        setWillShowSubscriptionStep(true)
        dispatch(
          updateAvaIntroIndex({
            ...questionIndex,
            questionCount: 6,
          })
        )
        return
      }

      try {
        const statusResponse = await subscriptionsController.checkStatus(
          interview.candidate.email
        )

        // If user has subscription AND can create interview, skip subscription step (05/05)
        // Otherwise, show subscription step (06/06)
        const willShow = !(
          statusResponse.has_subscription && statusResponse.can_create_interview
        )
        setWillShowSubscriptionStep(willShow)

        dispatch(
          updateAvaIntroIndex({
            ...questionIndex,
            questionCount: willShow ? 6 : 5,
          })
        )
      } catch (err) {
        // On error, default to showing subscription step (06/06)
        console.error('Error checking subscription for counter:', err)
        setWillShowSubscriptionStep(true)
        dispatch(
          updateAvaIntroIndex({
            ...questionIndex,
            questionCount: 6,
          })
        )
      }
    }

    checkSubscriptionForCounter()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interview?.candidate?.email])

  // Handle subscription success redirect
  useEffect(() => {
    let isMounted = true
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('subscription_success') === 'true') {
      // Refresh subscription status and continue
      const checkSubscription = async () => {
        try {
          const token = localStorage.getItem('token')
          if (token && isMounted) {
            httpClient.defaults.headers.Authorization = token
            await subscriptionsController.getSubscription()
            // Remove query param only if still mounted
            if (isMounted) {
              window.history.replaceState({}, '', window.location.pathname)
            }
          }
        } catch (err) {
          if (isMounted) {
            console.error('Error refreshing subscription:', err)
          }
        }
      }
      checkSubscription()
    }

    return () => {
      isMounted = false
    }
  }, [])

  const renderStep = (currentStep: IntroStep) => {
    const currentStepData = StepsData.find((s) => s.step === currentStep)
    switch (currentStep) {
      case IntroStep.UserName:
        return (
          <UserNameComponent
            backMode={backMode}
            goNext={async (candidate) => {
              // Save candidate info - no email validation required
              dispatch(
                avaSaveInfoAction({
                  interviewToken,
                  params: { to_intro: true },
                  candidate,
                })
              )
              goNextStep(currentStepData)
            }}
            goBack={() => goPrevStep(currentStepData)}
          />
        )
      case IntroStep.Subscription:
        return (
          <SubscriptionComponent
            backMode={backMode}
            goNext={() => goNextStep(currentStepData)}
            goBack={() => goPrevStep(currentStepData)}
            interviewToken={interviewToken}
          />
        )
      case IntroStep.Position:
        return (
          <PositionComponent
            backMode={backMode}
            goNext={(position) => {
              dispatch(avaSaveInfoAction({ interviewToken, position: { position } }))
              goNextStep(currentStepData)
            }}
            goBack={() => goPrevStep(currentStepData)}
          />
        )
      case IntroStep.Role:
        return (
          <RoleComponent
            backMode={backMode}
            goNext={(role) => {
              dispatch(
                avaSaveInfoAction({
                  interviewToken,
                  position: { role: role.label },
                })
              )
              if (role.is_manager) {
                setBackMode(false)
                dispatch(updateAvaIntroStepAction(IntroStep.IsManager))
                dispatch(
                  updateAvaIntroIndex({
                    ...questionIndex,
                    questionNumberText: '(Additional info)',
                  })
                )
              } else {
                goNextStep(currentStepData)
              }
            }}
            goBack={() => goPrevStep(currentStepData)}
          />
        )
      case IntroStep.IsManager:
        return (
          <ManagerComponent
            backMode={backMode}
            goNext={(isManager) => {
              dispatch(
                avaSaveInfoAction({ interviewToken, position: { is_manager: isManager } })
              )
              goNextStep(currentStepData)
            }}
            goBack={() => goPrevStep(currentStepData)}
          />
        )
      case IntroStep.IsPracticeForCompany:
        return (
          <PracticeForCompanyComponent
            backMode={backMode}
            goNext={(res) => {
              if (res) {
                setBackMode(false)
                dispatch(
                  avaSaveInfoAction({
                    interviewToken,
                    position: { is_specific_company: true },
                  })
                )
                dispatch(updateAvaIntroStepAction(IntroStep.PositionDetails))
                dispatch(
                  updateAvaIntroIndex({
                    ...questionIndex,
                    questionNumberText: '(Additional info)',
                  })
                )
              } else {
                dispatch(
                  avaSaveInfoAction({
                    interviewToken,
                    position: { is_specific_company: false },
                  })
                )
                goNextStep(currentStepData)
              }
            }}
            goBack={() => goPrevStep(currentStepData)}
          />
        )
      case IntroStep.PositionDetails:
        return (
          <PositionDetailsComponent
            backMode={backMode}
            goNext={({ company, qualifications, responsibilities }) => {
              dispatch(
                avaSaveInfoAction({
                  interviewToken,
                  position: {
                    company,
                    qualifications,
                    responsibilities,
                  },
                })
              )
              goNextStep(currentStepData)
            }}
            goBack={() => goPrevStep(currentStepData)}
          />
        )
      case IntroStep.Experience:
        return (
          <QuestionComponent
            backMode={backMode}
            text={SpeechTexts.ProfessionalBackground}
            subTitle={SpeechTexts.BetterUnderstandNeeds}
            title="My professional background"
            placeholder="You can copy and paste this information from your CV or LinkedIn profile"
            canGoBack
            noUrl
            optional
            value={interview.candidate?.experience}
            goNext={async (experience) => {
              // Save experience first
              dispatch(
                avaSaveInfoAction({
                  interviewToken,
                  candidate: { experience },
                })
              )

              // Check subscription status
              const { candidate } = interview
              if (candidate?.email) {
                try {
                  // Check subscription status by email (unauthenticated)
                  const statusResponse = await subscriptionsController.checkStatus(
                    candidate.email
                  )

                  if (statusResponse.has_subscription) {
                    // User has subscription
                    if (statusResponse.can_create_interview) {
                      // Can create interview, but user needs to be authenticated
                      // Check if already authenticated
                      const token = getAuthorizationToken()
                      if (token) {
                        // Already authenticated, proceed to interview
                        dispatch(
                          avaSaveInfoAction({
                            interviewToken,
                            params: { to_interview: true },
                          })
                        )
                        goNextStep(currentStepData) // Goes to GettingQuestions
                      } else {
                        // Not authenticated, show subscription step with message
                        goNextStep(currentStepData) // Goes to Subscription (will show message to log in)
                      }
                    } else {
                      // Has subscription but no interviews left - still go to subscription step to show error
                      goNextStep(currentStepData) // Goes to Subscription (will show error there)
                    }
                  } else {
                    // User doesn't have subscription, show subscription step
                    goNextStep(currentStepData) // Goes to Subscription
                  }
                } catch (error) {
                  console.error('Error checking subscription:', error)
                  // On error, show subscription step
                  goNextStep(currentStepData) // Goes to Subscription
                }
              } else {
                // No email, show subscription step
                goNextStep(currentStepData) // Goes to Subscription
              }
            }}
            goBack={() => goPrevStep(currentStepData)}
          />
        )
      case IntroStep.GettingQuestions:
        return (
          <GettingQuestionsComponent
            backMode={backMode}
            goNext={() => {
              playSound('/static/sound/ava-run-interview.wav')
              dispatch(updateAvaIntroIndex({ ...questionIndex, questionNumber: 0 }))
              dispatch(updateAvaStepAction(AvaStep.Interview))
            }}
          />
        )
      default:
        return 'Smth went wrong...'
    }
  }

  return (
    <Layout>
      <Header
        showInterviewPart
        showSettingsButton
        showBackButton={step !== IntroStep.GettingQuestions}
        interviewPart={InterviewPart.Intro}
        onBack={() => goPrevStep(StepsData.find((s) => s.step === step))}
      />
      {renderStep(step)}
      {isMobileOnly && <RotatePhoneComponent />}
      <SettingsBar />
      <audio id="ava-audio" />
      <InterviewLimitModal
        visible={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        hasSubscription={hasSubscription}
        interviewToken={interviewToken}
      />
    </Layout>
  )
}

export default IntroContainer
